import { IconButton, ListItem, ListItemIcon, ListItemSecondaryAction, ListItemText, ListSubheader } from '@material-ui/core';
import { Check, Delete, FormatListBulleted } from '@material-ui/icons';
import firebase from 'firebase/app';
import { useSnackbar } from 'notistack';
import { useHistory } from 'react-router';

import { Collections } from 'Collections';
import { FirebaseUtils } from 'firebaseUtils/FirebaseUtils';
import { AnalyticsEvent } from 'models/AnalyticsEvent';
import { AppUser } from 'models/AppUser';
import { Creator } from 'models/Creator';
import { removeRef } from 'models/FbDoc';
import { Grant } from 'models/Grant';
import { Nullable } from 'models/Nullable';
import { getOrgUserGrants, OrgUser } from 'models/OrgUser';
import { noPriceFieldNameText, PriceField } from 'models/PriceField';
import { PriceFieldSuggestionResolution, PriceFieldSuggestionResolutionStatus } from 'models/PriceFieldSuggestionResolution';
import { Paths } from 'Paths';

import { usePriceFieldSuggestions } from './PriceFieldSuggestionsProvider';

type Props = {
    orgUser?: Nullable<OrgUser>
    appUser?: Nullable<AppUser>
}

export function PriceFieldSugegstionsList({ orgUser, appUser }: Props) {
    const { enqueueSnackbar } = useSnackbar()
    const [suggestions] = usePriceFieldSuggestions()
    const grants = getOrgUserGrants(orgUser || null)
    const history = useHistory()

    const creator: Nullable<Creator> | undefined = appUser && orgUser && {
        orgUser,
        appUser,
    }

    const asList = suggestions && Object.values(suggestions)

    return (
        <>
            {!asList?.length ? null : (
                <>
                    <ListSubheader>Forslag til prisfelter</ListSubheader>
                    {asList?.map((suggestion) => {

                        const isOwnSuggestion = !!appUser && (suggestion.metadata.createdBy.appUser?.id === appUser?.id)
                        const canCreate = grants[Grant.CREATE_PRICE_FIELD]

                        return (
                            <ListItem key={suggestion.data.id}>
                                <ListItemIcon>
                                    <FormatListBulleted />
                                </ListItemIcon>
                                <ListItemText primary={noPriceFieldNameText(suggestion.data.name?.text)} />
                                <ListItemSecondaryAction>
                                    {canCreate && appUser && orgUser && creator ? (
                                        <IconButton
                                            color="primary"
                                            aria-label="Opprett Prisfelt"
                                            onClick={() => {
                                                FirebaseUtils.createDocument<PriceField>(
                                                    true,
                                                    Collections.PRICE_FIELDS,
                                                    suggestion.data.id,
                                                    appUser,
                                                    orgUser,
                                                    AnalyticsEvent.CREATED_PRICE_FIELD,
                                                    AnalyticsEvent.ACCEPTED_PRICE_FIELD_SUGGESTION,
                                                    AnalyticsEvent.CREATE_PRICE_FIELD_ERROR,
                                                    enqueueSnackbar,
                                                    'Prisfelt opprettet',
                                                    suggestion.data,
                                                    (doc) => ({ suggestionId: suggestion.data.id, priceFieldId: doc.id }),
                                                )
                                                FirebaseUtils.createDocument<PriceFieldSuggestionResolution>(
                                                    true,
                                                    Collections.PRICE_FIELD_SUGGESTION_RESOLUTIONS,
                                                    suggestion.data.id,
                                                    appUser,
                                                    orgUser,
                                                    AnalyticsEvent.ACCEPTED_PRICE_FIELD_SUGGESTION,
                                                    AnalyticsEvent.ACCEPTED_PRICE_FIELD_SUGGESTION,
                                                    AnalyticsEvent.ACCEPT_PRICE_FIELD_SUGGESTION_ERROR,
                                                    enqueueSnackbar,
                                                    null,
                                                    {
                                                        resolution: {
                                                            resolvedAt: firebase.firestore.Timestamp.now(),
                                                            resolvedBy: creator,
                                                            status: PriceFieldSuggestionResolutionStatus.ACCEPTED,
                                                        },
                                                        suggestion: {
                                                            priceField: removeRef(suggestion),
                                                        },
                                                    },
                                                    () => ({ suggestionId: suggestion.data.id }),
                                                )
                                                FirebaseUtils.deleteDocument(
                                                    true,
                                                    Collections.PRICE_FIELD_SUGGESTIONS,
                                                    suggestion.data.id,
                                                    appUser,
                                                    orgUser,
                                                    AnalyticsEvent.DELETED_PRICE_FIELD_SUGGESTION,
                                                    AnalyticsEvent.ACCEPTED_PRICE_FIELD_SUGGESTION,
                                                    AnalyticsEvent.DELETE_PRICE_FIELD_SUGGESTION_ERROR,
                                                    enqueueSnackbar,
                                                    null,
                                                    () => ({ suggestionId: suggestion.data.id }),
                                                )
                                                history.push(Paths.PRICE_FIELDS + '/' + suggestion.data.id)
                                            }}><Check /></IconButton>

                                    ) : null}
                                    {(appUser && orgUser && creator && (isOwnSuggestion || canCreate)) ? (
                                        <IconButton
                                            color="secondary"
                                            aria-label={isOwnSuggestion ? "Trekk tilbake forslag" : "Avvis forslag"}
                                            onClick={() => {
                                                if (!isOwnSuggestion) {
                                                    FirebaseUtils.createDocument<PriceFieldSuggestionResolution>(
                                                        true,
                                                        Collections.PRICE_FIELD_SUGGESTION_RESOLUTIONS,
                                                        suggestion.data.id,
                                                        appUser,
                                                        orgUser,
                                                        AnalyticsEvent.REJECTED_PRICE_FIELD_SUGGESTION,
                                                        AnalyticsEvent.REJECTED_PRICE_FIELD_SUGGESTION,
                                                        AnalyticsEvent.REJECT_PRICE_FIELD_SUGGESTION_ERROR,
                                                        enqueueSnackbar,
                                                        'Avviste forslaget',
                                                        {
                                                            resolution: {
                                                                resolvedAt: firebase.firestore.Timestamp.now(),
                                                                resolvedBy: creator,
                                                                status: PriceFieldSuggestionResolutionStatus.REJECTED,
                                                            },
                                                            suggestion: {
                                                                priceField: removeRef(suggestion),
                                                            },
                                                        },
                                                        () => ({ suggestionId: suggestion.data.id }),
                                                    )
                                                }
                                                FirebaseUtils.deleteDocument(
                                                    true,
                                                    Collections.PRICE_FIELD_SUGGESTIONS,
                                                    suggestion.data.id,
                                                    appUser,
                                                    orgUser,
                                                    AnalyticsEvent.DELETED_PRICE_FIELD_SUGGESTION,
                                                    isOwnSuggestion ? AnalyticsEvent.WITHDREW_PRICE_FIELD_SUGGESTION : AnalyticsEvent.REJECTED_PRICE_FIELD_SUGGESTION,
                                                    AnalyticsEvent.DELETE_PRICE_FIELD_SUGGESTION_ERROR,
                                                    enqueueSnackbar,
                                                    null,
                                                    () => ({ suggestionId: suggestion.data.id }),
                                                )
                                            }}><Delete /></IconButton>
                                    ) : null}
                                </ListItemSecondaryAction>
                            </ListItem>
                        )
                    })}
                </>
            )}
        </>
    )
}
